import React, { useState } from 'react';
import { Link, Plus, AlertCircle, CheckCircle, X, Film } from 'lucide-react';
import { addMovieFromTMDBUrl } from '../../services/tmdbService';

const TMDBUrlImporter = ({ isVisible, onClose, onAddMovie }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setError('Please enter a TMDB movie URL');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const movie = await addMovieFromTMDBUrl(url);
      onAddMovie([movie]); // Pass as array to match the interface
      setSuccess(`Successfully added "${movie.title}" to your collection!`);
      setUrl('');
      
      // Auto-close after success
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to add movie from URL');
      console.error('URL import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setError('');
    setSuccess('');
    onClose();
  };

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100 flex items-center gap-2">
              <Link size={20} />
              Add Movie from TMDB URL
            </h2>
            <button
              onClick={handleClose}
              className="text-orange-300 hover:text-orange-100 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-orange-200 text-sm mt-1">
            Paste a TMDB movie URL to add it directly to your collection
          </p>
        </div>

        <div className="p-6">
          {/* Instructions */}
          <div className="mb-6 p-4 bg-gray-700 border border-orange-500 rounded-lg">
            <h3 className="text-orange-300 font-semibold mb-2 flex items-center gap-2">
              <Film size={16} />
              How to use:
            </h3>
            <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
              <li>Go to <span className="text-orange-400 font-mono">themoviedb.org</span></li>
              <li>Search for and click on any movie</li>
              <li>Copy the URL from your browser</li>
              <li>Paste it below and click "Add Movie"</li>
            </ol>
            <div className="mt-3 text-xs text-gray-400">
              <strong>Example URLs:</strong>
              <div className="font-mono text-orange-300 mt-1">
                https://www.themoviedb.org/movie/694-the-shining<br/>
                https://themoviedb.org/movie/348-alien
              </div>
            </div>
          </div>

          {/* URL Input Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-orange-300 text-sm font-bold mb-2">
                TMDB Movie URL
              </label>
              <input
                type="url"
                value={url}
                onChange={handleUrlChange}
                placeholder="https://www.themoviedb.org/movie/694-the-shining"
                className="w-full px-4 py-3 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50"
                disabled={loading}
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-3 bg-red-900 border border-red-600 rounded-lg flex items-center gap-2">
                <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                <span className="text-red-200 text-sm">{error}</span>
              </div>
            )}

            {/* Success Display */}
            {success && (
              <div className="p-3 bg-green-900 border border-green-600 rounded-lg flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
                <span className="text-green-200 text-sm">{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !url.trim()}
                className="bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
              >
                <Plus size={16} />
                {loading ? 'Adding Movie...' : 'Add Movie'}
              </button>
            </div>
          </form>

          {/* Loading State */}
          {loading && (
            <div className="mt-4 text-center py-4">
              <div className="text-orange-400 mb-2">
                <Film className="w-8 h-8 mx-auto animate-pulse" />
              </div>
              <p className="text-gray-300">Fetching movie data from TMDB...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TMDBUrlImporter;
